import type { InfiniteData } from '@tanstack/react-query'
import type { ProductSearchOptions } from './productSearchOptions'
import type { ProductOrder } from '@/contexts/products/ProductsContext'
import type { GalleryProducts, ProductByViewMode, ViewMode } from '@/types/catalog'
import { getProducts, PRODUCTS_PAGE_SIZE } from '@/api'
import { useInfiniteScrollQuery } from '@/hooks/useInfiniteScrollQuery'
import { queryKeys } from '@/query/queryKeys'

export interface UseProductsInfiniteQueryOptions<V extends ViewMode = ViewMode> extends ProductSearchOptions<V> {
  universeId: number
  departmentId: number
  isFullDepartment: boolean
  enabled?: boolean
  order?: ProductOrder
}

export function useProductsInfiniteQuery<V extends ViewMode = 'sm'>({
  season,
  universeId,
  departmentId,
  subdepartmentIds,
  isFullDepartment,
  familiesIds,
  viewMode,
  productCode,
  currentLanguage = 'en',
  filters = {},
  isNew = false,
  enabled = true,
  order = 'SEGMENTATION_ASC',
}: UseProductsInfiniteQueryOptions<V>) {
  type TGallery = GalleryProducts<ProductByViewMode<V>>

  const {
    pages,
    sentinelRef,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isLoading,
    isError,
  } = useInfiniteScrollQuery<TGallery>({
    queryKey: queryKeys.productsInfinite({
      universeId,
      departmentId,
      subdepartmentIds,
      isFullDepartment,
      familiesIds,
      viewMode,
      productCode,
      filters,
      isNew,
      order,
    }),
    enabled,
    queryFn: async ({ pageParam, signal }) => {
      if (!signal?.aborted) {
        const response = await (
          getProducts<TGallery>({
            season,
            ...isFullDepartment && { department: departmentId },
            subdepartments: subdepartmentIds,
            families: familiesIds,
            viewMode,
            productCode,
            currentLanguage,
            filters,
            isNew,
            page: pageParam,
            size: PRODUCTS_PAGE_SIZE,
            order,
          }))
        return {
          ...response,
          data: response.data.filter(data => data !== null && data.family !== null),
        }
      }
      throw new Error('Request aborted')
    },
    placeholderData: (previousData: InfiniteData<TGallery, number> | undefined) => previousData,
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.page_request.page_index
      return currentPage + 1 < lastPage.total_pages ? currentPage + 1 : undefined
    },
  })

  const products = pages.flatMap(page => page.data)

  return { products, pages, sentinelRef, isFetching, isFetchingNextPage, hasNextPage, isLoading, isError }
}
